import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  createComponentType,
  deleteComponentType,
  getComponentTypes,
  updateComponentType,
} from '../api/component-typeAPI';
import { fetchData } from '../utils/apiHelpers';

const ComponentTypeLayer = () => {
  const userProfile = useUserProfile();
  const [types, setTypes] = useState([]);
  const [newType, setNewType] = useState({ coreReturn: false, priority: 1 });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const fetchTypes = () => {
    fetchData(getComponentTypes, {}, setTypes);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!newType.name?.trim()) newErrors.name = 'Component Type is required';
    if (newType.priority === undefined || newType.priority === null || newType.priority === '') {
      newErrors.priority = 'Priority is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchTypes();
  }, []);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.destroy();
      dtRef.current = null;
    }
    if (types.length > 0) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: {
          paginate: { first: '', last: '', next: '', previous: '' },
        },
      });
    }
    return () => {
      if (dtRef.current) {
        dtRef.current.destroy(true);
        dtRef.current = null;
      }
    };
  }, [types]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewType((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleEdit = (item) => {
    setNewType({ ...item, coreReturn: !!item.coreReturn });
    setEditingId(item.id);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this component type?')) return;
    try {
      await deleteComponentType(id);
      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }
      await fetchTypes();
    } catch (error) {
      console.error('Error deleting component type:', error);
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      const payload = {
        name: newType.name,
        coreReturn: !!newType.coreReturn,
        priority: Number(newType.priority) || 1,
      };

      if (editingId) {
        await updateComponentType(editingId, payload);
      } else {
        await createComponentType(payload);
      }

      setShowModal(false);
      setEditingId(null);

      if (dtRef.current) {
        dtRef.current.destroy();
        dtRef.current = null;
      }

      await fetchTypes();
      setNewType({ coreReturn: false, priority: 1 });
    } catch (error) {
      console.error('Error saving component type:', error);
    }
  };

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-header d-flex justify-content-between align-items-center p-3">
        <input
          type="text"
          className="form-control w-auto"
          placeholder="Search..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {userProfile?.privileges?.includes('component-type:create') && (
          <button
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            onClick={() => {
              setNewType({ coreReturn: false, priority: 1 });
              setEditingId(null);
              setShowModal(true);
            }}
          >
            <Icon icon="ic:baseline-plus" className="icon text-xl" />
            Add Component Type
          </button>
        )}
      </div>

      <div className="card-body p-0">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table mb-0 w-100 rounded-0" ref={tableRef}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Core Return</th>
                <th>Priority</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {types.map((item) => (
                <tr key={item.id}>
                  <td><strong>{item.name}</strong></td>
                  <td>{item.coreReturn ? 'Yes' : 'No'}</td>
                  <td>{item.priority}</td>
                  <td>
                    <div className="d-flex gap-2">
                      {userProfile?.privileges?.includes('component-type:update') && (
                        <button
                          className="w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleEdit(item)}
                        >
                          <Icon icon="ic:baseline-edit" className="icon text-xl line-height-1" />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('component-type:delete') && (
                        <button
                          className="w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center"
                          onClick={() => handleDelete(item.id)}
                        >
                          <Icon icon="ic:baseline-delete-outline" className="icon text-xl line-height-1" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal show d-block" tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{editingId ? 'Edit Component Type' : 'New Component Type'}</h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)} />
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label">Name</label>
                    <input
                      type="text"
                      className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                      name="name"
                      value={newType.name || ''}
                      onChange={handleInputChange}
                    />
                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                  </div>
                  <div className="col-md-3 d-flex align-items-center">
                    <div className="form-check mt-3">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="coreReturn"
                        name="coreReturn"
                        checked={!!newType.coreReturn}
                        onChange={handleInputChange}
                      />
                      <label className="form-check-label" htmlFor="coreReturn">
                        Core Return Required
                      </label>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Priority</label>
                    <input
                      type="number"
                      className={`form-control ${errors.priority ? 'is-invalid' : ''}`}
                      name="priority"
                      value={newType.priority ?? ''}
                      onChange={handleInputChange}
                    />
                    {errors.priority && <div className="invalid-feedback">{errors.priority}</div>}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSave}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComponentTypeLayer;
